import { Chip } from "@/components/Chip";
import type { RecordedSessionSummary } from "./shared";

export const MAX_REQUEST_COUNT = 10_000;

export interface KubernetesRecordingSummary extends RecordedSessionSummary {
  requestCount: number;
  recordingIncomplete: boolean;
}

export interface KubernetesRequestSummary {
  id: string;
  startedAt: string;
  durationMs: number;
  verb: string;
  category: string;
  apiGroup: string | null;
  resource: string;
  subresource: string | null;
  namespace: string | null;
  name: string | null;
  statusCode: number;
  command: string | null;
  recording: string | null;
  recordingId: string | null;
  recordingTruncated: boolean;
}

export const REQUEST_TYPE_OPTIONS = [
  { value: "", label: "All requests" },
  { value: "change", label: "Changes" },
  { value: "access", label: "Shells and port-forwards" },
  { value: "denied", label: "Denied" },
];

const STATUS_TEXT: Record<number, string> = {
  101: "Opened",
  200: "OK",
  201: "Created",
  202: "Accepted",
  204: "No Content",
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  409: "Conflict",
  422: "Invalid",
  429: "Too Many Requests",
  500: "Server Error",
  503: "Unavailable",
};

export function StatusChip({ statusCode }: { statusCode: number }) {
  const text = STATUS_TEXT[statusCode];

  return (
    <Chip variant={statusCode >= 400 ? "warning" : "default"}>
      <span className="tabular-nums">
        {text ? `${statusCode} ${text}` : statusCode}
      </span>
    </Chip>
  );
}

export function Verb({ request }: { request: KubernetesRequestSummary }) {
  return (
    <span
      className={`font-mono text-xs ${request.category === "read" ? "text-foreground/70" : "font-semibold text-foreground"}`}
    >
      {request.verb}
    </span>
  );
}

export function resourceLabel(request: KubernetesRequestSummary): string {
  return request.subresource && request.category !== "access"
    ? `${request.resource}/${request.subresource}`
    : request.resource;
}

export function ResourceName({
  request,
}: {
  request: KubernetesRequestSummary;
}) {
  return (
    <>
      <span className="font-medium">{resourceLabel(request)}</span>
      {request.name && (
        <span className="font-mono text-xs"> {request.name}</span>
      )}
      {request.command && (
        <span className="font-mono text-xs text-foreground/70">
          {" "}
          {request.command}
        </span>
      )}
    </>
  );
}
